import {DateTime} from 'luxon';
import {z} from 'zod';
import {browserTimeZone, readStoredJson} from '@brunata-angular/util';
import {
    defaultExportColumns,
    exportColumnStateSchema,
    headerRowModeSchema,
    reconcileExportColumns,
    unitPlacementSchema,
} from '../shared/models/export-columns.model';
import {
    defaultExportFileFormat,
    exportFileFormatSchema,
} from '../shared/models/export-file-format.model';
import {
    maxScheduleDayOfMonth,
    maxScheduleInterval,
    minScheduleInterval,
    savedExportDeliveryChannelSchema,
    savedExportRecurrenceSchema,
    savedExportSchedulePeriodSchema,
    savedExportScheduleUnitSchema,
    savedExportUsageSchema,
    savedExportWeekdaySchema,
} from '../saved-exports/saved-export.model';
import {
    defaultDaysBackInTime,
    defaultReadingResolution,
    maxDaysBackInTime,
    minDaysBackInTime,
    readingResolutionSchema,
} from '../shared/models/reading-shape.model';

// A wizard's work in progress toward a SavedExportCreateRequest. `usage` is draft-only state: the
// backend encodes on-demand as recurrence null.
export const savedExportDraftSchema = z.object({
    name: z.string(),
    // Drafts stored before the usage choice existed lack the key; default them to the default flow.
    usage: savedExportUsageSchema.default('on-demand'),
    // Drafts stored before the schedule step existed lack these keys (AGI-19086).
    recurrence: savedExportRecurrenceSchema.default('WEEKLY_EVERY_MONDAY_8AM'),
    startDate: z.string().nullable().default(null),
    recipients: z.array(z.string()).catch([]),
    // Drafts stored before SFTP delivery existed lack these keys (AGI-19486).
    deliveryChannel: savedExportDeliveryChannelSchema.catch('EMAIL'),
    sftpConnectionId: z.string().nullable().catch(null),
    sftpSubfolder: z.string().nullable().catch(null),
    fileNamePattern: z.string().nullable().catch(null),
    customSchedule: z.boolean().catch(false),
    scheduleUnit: savedExportScheduleUnitSchema.catch('DAYS'),
    scheduleInterval: z
        .number()
        .int()
        .min(minScheduleInterval)
        .max(maxScheduleInterval)
        .catch(minScheduleInterval),
    scheduleWeekdays: z.array(savedExportWeekdaySchema).catch([]),
    scheduleDayOfMonth: z.number().int().min(1).max(maxScheduleDayOfMonth).catch(1),
    schedulePeriod: savedExportSchedulePeriodSchema.catch('DAYS_BACK'),
    formatId: z.string().default('csv'),
    // Drafts stored before super-type and channel selection existed lack these keys (AGI-18792).
    superTypes: z.array(z.string()).default([]),
    // Models are keyed without their vendor name, which only reaches the channels endpoint with
    // AGI-19929's backend half; a channel id is built from fields that were there all along.
    modelKeys: z.array(z.string()).default([]),
    channelIds: z.array(z.string()).default([]),
    offeredModelKeys: z.array(z.string()).default([]),
    offeredChannelIds: z.array(z.string()).default([]),
    channelLess: z.record(z.string(), z.boolean()).default({}),
    // Drafts stored before system selection existed lack the key (AGI-18793); default to an empty
    // selection.
    systemIds: z.array(z.string()).default([]),
    autoIncludeFutureSystems: z.boolean().default(false),
    fileFormat: exportFileFormatSchema.catch({...defaultExportFileFormat}),
    headerRow: headerRowModeSchema.catch('EVERY_EXPORT'),
    unitPlacement: unitPlacementSchema.catch('OWN_COLUMN'),
    columns: z.array(exportColumnStateSchema).catch([...defaultExportColumns]),
    timeZone: z.string().default(browserTimeZone()),
    includeDismountedMeters: z.boolean().default(false),
    readingResolution: readingResolutionSchema.catch(defaultReadingResolution),
    daysBackInTime: z
        .number()
        .int()
        .min(minDaysBackInTime)
        .max(maxDaysBackInTime)
        .nullable()
        .catch(defaultDaysBackInTime),
});

export type SavedExportDraft = z.infer<typeof savedExportDraftSchema>;

export type SavedExportScheduleDraft = Pick<
    SavedExportDraft,
    | 'recurrence'
    | 'startDate'
    | 'recipients'
    | 'deliveryChannel'
    | 'sftpConnectionId'
    | 'sftpSubfolder'
    | 'fileNamePattern'
    | 'customSchedule'
    | 'scheduleUnit'
    | 'scheduleInterval'
    | 'scheduleWeekdays'
    | 'scheduleDayOfMonth'
    | 'schedulePeriod'
>;

/** The draft's schedule slice as the schedule form group holds it. */
export function scheduleFormValue(
    draft: SavedExportScheduleDraft,
): Omit<SavedExportScheduleDraft, 'startDate'> & {startDate: DateTime | null} {
    const {startDate, ...rest} = draft;
    return {...rest, startDate: startDate === null ? null : DateTime.fromISO(startDate)};
}

/** The schedule form group's value as the draft stores it. */
export function scheduleDraftValue(
    schedule: Omit<SavedExportScheduleDraft, 'startDate'> & {startDate: DateTime | null},
): SavedExportScheduleDraft {
    const {startDate, ...rest} = schedule;
    return {...rest, startDate: startDate?.toISODate() ?? null};
}

export const emptySavedExportDraft: SavedExportDraft = {
    name: '',
    usage: 'on-demand',
    recurrence: 'WEEKLY_EVERY_MONDAY_8AM',
    startDate: null,
    recipients: [],
    deliveryChannel: 'EMAIL',
    sftpConnectionId: null,
    sftpSubfolder: null,
    fileNamePattern: null,
    customSchedule: false,
    scheduleUnit: 'DAYS',
    scheduleInterval: 1,
    scheduleWeekdays: [],
    scheduleDayOfMonth: 1,
    schedulePeriod: 'DAYS_BACK',
    formatId: 'csv',
    superTypes: [],
    modelKeys: [],
    channelIds: [],
    offeredModelKeys: [],
    offeredChannelIds: [],
    channelLess: {},
    systemIds: [],
    autoIncludeFutureSystems: false,
    fileFormat: {...defaultExportFileFormat},
    headerRow: 'EVERY_EXPORT',
    unitPlacement: 'OWN_COLUMN',
    columns: [...defaultExportColumns],
    timeZone: browserTimeZone(),
    includeDismountedMeters: false,
    readingResolution: defaultReadingResolution,
    daysBackInTime: defaultDaysBackInTime,
};

// v4 discards pre-release drafts that selected channels per vendor model instead of picking vendor
// models and channels separately, and those that keyed models by vendor name (AGI-19929).
const STORAGE_KEY = 'customized-exports.draft-template.v4';

// A draft without a start date gets today (like quick exports), so the schedule step is prefilled
// and the prefill alone does not count as an unsaved change.
export function readStoredDraft(): SavedExportDraft {
    const stored = readStoredJson(STORAGE_KEY, savedExportDraftSchema, emptySavedExportDraft);
    return {
        ...stored,
        startDate: stored.startDate ?? DateTime.now().startOf('day').toISODate(),
        columns: reconcileExportColumns(stored.columns),
    };
}

export function writeStoredDraft(draft: SavedExportDraft): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
}

export function clearStoredDraft(): void {
    localStorage.removeItem(STORAGE_KEY);
}
